import { useCallback, useState } from "react";
import { TableColumn } from "@/components/ui/Table";
import {
  downloadExcel,
  downloadMultiSheetExcel,
  ExcelDownloadOptions,
  SheetData,
} from "@/lib/excel";

interface UseExcelDownloadOptions extends ExcelDownloadOptions {
  /** 다운로드 전 콜백 */
  onBeforeDownload?: () => void;
  /** 다운로드 후 콜백 */
  onAfterDownload?: () => void;
  /** 에러 발생 시 콜백 */
  onError?: (error: Error) => void;
}

interface UseExcelDownloadReturn<T> {
  /** 엑셀 다운로드 실행 함수 */
  download: (data: T[], columns: TableColumn[]) => void;
  /** 다중 시트 엑셀 다운로드 실행 함수 */
  downloadMultiSheet: (sheets: SheetData<T>[]) => void;
  /** 다운로드 진행 중 여부 */
  isDownloading: boolean;
}

/**
 * 엑셀 다운로드 훅
 *
 * @param options - 다운로드 옵션
 * @returns 다운로드 함수 및 상태
 *
 * @example
 * // 기본 사용
 * const { download, isDownloading } = useExcelDownload({
 *   fileName: "매출현황",
 * });
 *
 * // 다운로드 실행
 * <Button onClick={() => download(tableData, columns)} disabled={isDownloading}>
 *   엑셀
 * </Button>
 *
 * @example
 * // 콜백 사용
 * const { download } = useExcelDownload({
 *   fileName: "거래내역",
 *   onBeforeDownload: () => console.log("다운로드 시작"),
 *   onAfterDownload: () => toast.success("다운로드 완료"),
 *   onError: (err) => toast.error(err.message),
 * });
 */
export const useExcelDownload = <T extends Record<string, any>>(
  options: UseExcelDownloadOptions = {}
): UseExcelDownloadReturn<T> => {
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    fileName,
    sheetName,
    includeGroupHeader,
    includeDateInFileName,
    onBeforeDownload,
    onAfterDownload,
    onError,
  } = options;

  const download = useCallback(
    (data: T[], columns: TableColumn[]) => {
      if (isDownloading) return;

      try {
        setIsDownloading(true);
        onBeforeDownload?.();

        downloadExcel(data, columns, {
          fileName,
          sheetName,
          includeGroupHeader,
          includeDateInFileName,
        });

        onAfterDownload?.();
      } catch (error) {
        console.error("엑셀 다운로드 실패:", error);
        onError?.(error instanceof Error ? error : new Error(String(error)));
      } finally {
        setIsDownloading(false);
      }
    },
    [
      isDownloading,
      fileName,
      sheetName,
      includeGroupHeader,
      includeDateInFileName,
      onBeforeDownload,
      onAfterDownload,
      onError,
    ]
  );

  const downloadMultiSheet = useCallback(
    (sheets: SheetData<T>[]) => {
      if (isDownloading) return;

      try {
        setIsDownloading(true);
        onBeforeDownload?.();

        downloadMultiSheetExcel(sheets, fileName, includeDateInFileName);

        onAfterDownload?.();
      } catch (error) {
        console.error("엑셀 다운로드 실패:", error);
        onError?.(error instanceof Error ? error : new Error(String(error)));
      } finally {
        setIsDownloading(false);
      }
    },
    [isDownloading, fileName, includeDateInFileName, onBeforeDownload, onAfterDownload, onError]
  );

  return {
    download,
    downloadMultiSheet,
    isDownloading,
  };
};

export default useExcelDownload;
